import { defineStore } from "pinia";
import Keycloak from "keycloak-js";
import { Capacitor } from "@capacitor/core";
import base_url from "../store/base_url.json";
import { createNativeKeycloak } from "../keycloak/nativeOidcManager.js";
import { reduce } from "lodash";

const DEFAULT_TOKEN_REFRESH_SECONDS = 70;
const INIT_TIMEOUT_MS = 30000;
const USERINFO_TIMEOUT_MS = 8000;
const TOKENS_STORAGE_KEY = "keycloakTokens";
const HAS_LOGGED_IN_KEY = "avocashHasLoggedIn";

function withTimeout(promise, ms, message) {
  let handle;
  const timeout = new Promise((_, reject) => {
    handle = setTimeout(
      () => reject(new Error(message || "Operation timed out")),
      ms
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(handle));
}

function randomBytesBase64Url(length) {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

// Pure-JS SHA-256 (hex). Deterministic and synchronous, so PKCE generation can
// never hang on crypto.subtle (which keycloak-js's own login relies on).
function sha256Hex(input) {
  const maxWord = 4294967296; // 2^32
  const asciiBitLength = input.length * 8;
  const words = [];
  let hash = [];
  const k = [];
  const primes = [];
  const composite = {};
  for (let candidate = 2; primes.length < 64; candidate++) {
    if (!composite[candidate]) {
      for (let i = 0; i < 313; i += candidate) composite[i] = candidate;
      primes.push(candidate);
    }
  }
  for (let i = 0; i < 64; i++) {
    hash[i] = (Math.sqrt(primes[i]) * maxWord) | 0;
    k[i] = (Math.cbrt(primes[i]) * maxWord) | 0;
  }
  let chunks = `${input}\x80`;
  while ((chunks.length % 64) - 56) chunks += "\x00";
  for (let i = 0; i < chunks.length; i++) {
    const b = chunks.charCodeAt(i);
    if (b >> 8) throw new Error("SHA-256 input must be ASCII");
    words[i >> 2] |= b << (((3 - i) % 4) * 8);
  }
  words[words.length] = (asciiBitLength / maxWord) | 0;
  words[words.length] = asciiBitLength;
  const rotr = (x, n) => (x >>> n) | (x << (32 - n));
  let result = "";
  for (let j = 0; j < words.length; ) {
    const w = words.slice(j, (j += 16));
    const oldHash = hash.slice(0);
    hash = hash.slice(0, 8);
    for (let i = 0; i < 64; i++) {
      const w15 = w[i - 15];
      const w2 = w[i - 2];
      const a = hash[0];
      const e = hash[4];
      const temp1 =
        hash[7] +
        (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) +
        ((e & hash[5]) ^ (~e & hash[6])) +
        k[i] +
        (w[i] =
          i < 16
            ? w[i]
            : (w[i - 16] +
                (rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3)) +
                w[i - 7] +
                (rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10))) |
              0);
      const temp2 =
        (rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) +
        ((a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2]));
      hash = [(temp1 + temp2) | 0].concat(hash);
      hash[4] = (hash[4] + temp1) | 0;
    }
    for (let i = 0; i < 8; i++) {
      hash[i] = (hash[i] + oldHash[i]) | 0;
    }
  }
  for (let i = 0; i < 8; i++) {
    for (let j = 3; j + 1; j--) {
      const b = (hash[i] >> (j * 8)) & 255;
      result += (b < 16 ? "0" : "") + b.toString(16);
    }
  }
  return result;
}

function sha256Base64Url(text) {
  const hex = sha256Hex(text);
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function getTokenStorage() {
  if (typeof window !== "undefined") {
    return window.__piniaLcStorage || null;
  }
  return null;
}

export const useKeycloakStore = defineStore("keycloak", {
  state: () => ({
    keycloak: null,
    locale: navigator.language?.split("-")[0] || "en",
    initialized: false,
    authenticated: false,
    token: "",
    refreshToken: "",
    idToken: "",
    tokenParsed: null,
    timeSkew: 0,
    userInfo: null,
    subject: null,
    profile: null,
    user: null,
    initError: null,
    isNativePlatform: Capacitor.isNativePlatform(),
    _updateInterval: null,
    _initializing: null,
    _profileLoading: null,
    _coldStartCompleted: false,
  }),

  getters: {
    isAuthenticated: (state) => state.authenticated,
    accessToken: (state) => state.token,
    keycloakInstance: (state) => state.keycloak,
    hasLoggedIn: (state) => {
      const storage = getTokenStorage();
      if (!storage) return false;
      try {
        return !!storage.get(HAS_LOGGED_IN_KEY);
      } catch (error) {
        return false;
      }
    },
  },

  actions: {
    createInstance(config = base_url.keycloakJson) {
      if (!this.keycloak) {
        if (this.isNativePlatform) {
          this.keycloak = createNativeKeycloak(config);
        } else {
          this.keycloak = new Keycloak(config);
        }
      }
      this.bindEvents();

      if (typeof window !== "undefined") {
        window._keycloak = this.keycloak;
      }

      return this.keycloak;
    },

    bindEvents() {
      if (!this.keycloak) return;

      this.keycloak.onReady = (authenticated) => {
        this.authenticated = !!authenticated;
        this._syncFromInstance();
        if (authenticated) {
          this._persistTokens();
          this._startTokenRefresh();
        } else {
          this._stopTokenRefresh();
        }
      };

      this.keycloak.onAuthSuccess = () => {
        this._syncFromInstance();
        this._persistTokens();
        this._startTokenRefresh();
        // Populate profile (banner, seller hub, country/currency defaults)
        void this.loadUserProfile().catch((error) => {
          console.warn("Failed to load user profile", error);
        });
      };

      this.keycloak.onAuthRefreshSuccess = () => {
        this._syncFromInstance();
        this._persistTokens();
      };

      this.keycloak.onAuthRefreshError = () => {
        this._stopTokenRefresh();
        this.authenticated = false;
        this._clearPersistedTokens();
        this.initError = new Error("Keycloak token refresh failed");
        try {
          import("../session-expiry.js").then((m) => m.markSessionExpiry()).catch(() => {});
        } catch {}
      };

      this.keycloak.onAuthError = (errorData) => {
        console.warn("Keycloak auth error", errorData);
        this._stopTokenRefresh();
        this.authenticated = false;
      };

      this.keycloak.onAuthLogout = () => {
        this._stopTokenRefresh();
        this.authenticated = false;
        this._clearPersistedTokens();
      };

      this.keycloak.onTokenExpired = async () => {
        try {
          await this.updateToken(20);
        } catch (error) {
          console.error("Keycloak token expired and refresh failed", error);
          this._clearPersistedTokens();
          try {
            const { markSessionExpiry } = await import("../session-expiry.js");
            markSessionExpiry();
          } catch {}
        }
      };
    },

    _loadPersistedTokens() {
      if (this.isNativePlatform) {
        return null;
      }
      const storage = getTokenStorage();
      if (!storage) return null;
      try {
        return storage.get(TOKENS_STORAGE_KEY) || null;
      } catch (error) {
        return null;
      }
    },

    _persistTokens() {
      if (!this.isNativePlatform) {
        return;
      }
      this._setHasLoggedInFlag();
    },

    _setHasLoggedInFlag() {
      const storage = getTokenStorage();
      if (!storage) return;
      try {
        storage.set(HAS_LOGGED_IN_KEY, true);
      } catch (error) {
        console.error("Failed to persist logged-in flag", error);
      }
    },

    _clearPersistedTokens() {
      if (this.isNativePlatform) {
        if (this.keycloak && typeof this.keycloak.clearKeychainState === "function") {
          void this.keycloak.clearKeychainState();
        }
        return;
      }
      const storage = getTokenStorage();
      if (!storage) return;
      try {
        storage.remove(TOKENS_STORAGE_KEY);
      } catch (error) {
        console.error("Failed to clear persisted keycloak tokens", error);
      }
    },

    _syncFromInstance() {
      if (!this.keycloak) return;

      this.authenticated = !!this.keycloak.authenticated;
      this.token = this.keycloak.token || "";
      this.refreshToken = this.keycloak.refreshToken || "";
      this.idToken = this.keycloak.idToken || "";
      this.tokenParsed = this.keycloak.tokenParsed || null;
      this.timeSkew = Number(this.keycloak.timeSkew) || 0;
      this.userInfo = this.keycloak.userInfo || null;
      this.subject = this.keycloak.subject || null;

      if (typeof window !== "undefined") {
        window._keycloak = this.keycloak;
      }
    },

    _getRedirectUri() {
      if (Capacitor.getPlatform() === "ios") {
        return import.meta.env.VITE_AVOCASH_ENABLE_IOS_UNIVERSAL_LINK === "true"
          ? import.meta.env.VITE_AVOCASH_UNIVERSAL_LINK_BASE ||
              "https://avocash.com/login"
          : import.meta.env.VITE_AVOCASH_MOBILE_URL_SCHEME || "avocash://login";
      }
      return (
        import.meta.env.VITE_AVOCASH_UNIVERSAL_LINK_BASE ||
        "https://avocash.com/login"
      );
    },

    _getInitOptions(options = {}) {
      const locale = navigator.language?.split("-")[0] || "en";
      const persistedTokens = this._loadPersistedTokens();
      const restoredTokens =
        !this._coldStartCompleted &&
        persistedTokens?.token && persistedTokens?.refreshToken
          ? {
              token: persistedTokens.token,
              refreshToken: persistedTokens.refreshToken,
              idToken: persistedTokens.idToken,
            }
          : {};

      const defaultOptions = Capacitor.isNativePlatform()
        ? {
            checkLoginIframe: false,
            silentCheckSsoFallback: false,
            redirectUri: this._getRedirectUri(),
            responseMode: "query",
            responseType: "code",
            enableLogging: true,
            flow: "standard",
            pkceMethod: "S256",
            scope: "openid profile basic phone email",
            ...restoredTokens,
            locale,
          }
        : {
            onLoad: "check-sso",
            adapter: "default",
            checkLoginIframe: false,
            silentCheckSsoFallback: true,
            silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
            redirectUri: window.location.origin + window.location.pathname,
            promiseType: "native",
            responseMode: "query",
            responseType: "code",
            flow: "standard",
            scope: "openid profile basic phone email",
            pkceMethod: "S256",
            locale,
            enableLogging: true,
          };

      return {
        ...defaultOptions,
        ...options,
      };
    },

    _stopTokenRefresh() {
      if (this._updateInterval) {
        clearInterval(this._updateInterval);
        this._updateInterval = null;
      }
    },

    _startTokenRefresh() {
      this._stopTokenRefresh();

      if (!this.keycloak || !this.authenticated) {
        return;
      }

      this._updateInterval = setInterval(() => {
        void this.updateToken(DEFAULT_TOKEN_REFRESH_SECONDS).catch((error) => {
          console.error("Silent token refresh failed", error);
        });
      }, 5000);
    },

    async initialize(config = base_url.keycloakJson, options = null) {
      if (this.initialized && this.keycloak) {
        return this.authenticated;
      }

      if (this._initializing) {
        return this._initializing;
      }

      const doInit = async () => {
        this.createInstance(config ?? base_url.keycloakJson);

        try {
          const initOptions = this.isNativePlatform
            ? {}
            : options
              ? this._getInitOptions(options)
              : this._getInitOptions();
          const initPromise = this.keycloak.init(initOptions);

          let initTimer;
          const authenticated = await Promise.race([
            initPromise,
            new Promise((_, reject) => {
              initTimer = setTimeout(() => {
                reject(new Error(`Keycloak initialization timed out after ${INIT_TIMEOUT_MS}ms`));
              }, INIT_TIMEOUT_MS);
            }),
          ]);

          clearTimeout(initTimer);
          this.initialized = true;
          this._coldStartCompleted = false;
          this._syncFromInstance();

          if (this.authenticated) {
            this._startTokenRefresh();
            // Populate profile for consumers (banner, seller hub, defaults)
            // before the app mounts, bounded so a stalled account endpoint
            // can never block boot (the load continues in the background).
            await withTimeout(
              this.loadUserProfile(),
              4000,
              "User profile load timed out",
            ).catch((error) => {
              console.warn("Failed to load user profile", error);
            });
          } else {
            this._stopTokenRefresh();
          }

          return authenticated;
        } catch (error) {
          this._coldStartCompleted = false;
          this.initError = error;
          this.authenticated = false;
          this._stopTokenRefresh();
          throw error;
        }
      };

      if (Capacitor.isNativePlatform()) {
        this._initializing = new Promise((resolve, reject) => {
          const start = () => {
            doInit()
              .then(resolve)
              .catch(reject)
              .finally(() => {
                this._initializing = null;
              });
          };

          if (
            document.readyState === "complete" ||
            document.readyState === "interactive"
          ) {
            start();
          } else {
            document.addEventListener("deviceready", start, { once: true });
          }
        });
      } else {
        this._initializing = doInit().finally(() => {
          this._initializing = null;
        });
      }

      return this._initializing;
    },

    async updateToken(minValidity = DEFAULT_TOKEN_REFRESH_SECONDS) {
      if (!this.keycloak) return false;
      if (!this.keycloak.authenticated || !this.keycloak.refreshToken) {
        this._stopTokenRefresh();
        return false;
      }

      try {
        const refreshed = await this.keycloak.updateToken(minValidity);
        this._syncFromInstance();
        return refreshed;
      } catch (error) {
        console.error("Failed to refresh token", error?.message || error);
        this.authenticated = false;
        this._stopTokenRefresh();
        this._clearPersistedTokens();
        try {
          import("../session-expiry.js").then((m) => m.markSessionExpiry()).catch(() => {});
        } catch {}
        throw error;
      }
    },

    async login(options) {
      if (!this.keycloak) {
        throw new Error("Keycloak instance is not initialized");
      }
      const kcOptions =
        typeof options === "string"
          ? { redirectUri: options, locale: this.locale }
          : { ...options, locale: this.locale };

      if (this.isNativePlatform) {
        return await this.keycloak.login(kcOptions);
      }

      // Web/PWA: minimal deterministic OIDC authorize redirect. We build the
      // URL and persist the callback state ourselves instead of relying on
      // keycloak-js's internal login(), which can stall before navigating.
      return this._webLogin(kcOptions);
    },

    async _webLogin(options) {
      const kc = this.keycloak;
      const redirectUri =
        options.redirectUri || kc.redirectUri || window.location.href;

      const state = randomBytesBase64Url(16);
      const nonce = randomBytesBase64Url(16);

      // PKCE (S256), required by the Keycloak server for this client. The
      // verifier is persisted in the callback state so keycloak-js includes
      // it (as code_verifier) during the token exchange on return.
      const codeVerifier = randomBytesBase64Url(48);
      const codeChallenge = sha256Base64Url(codeVerifier);

      let scope = options.scope || kc.scope || "openid";
      const scopeValues = String(scope).split(" ");
      if (!scopeValues.includes("openid")) {
        scopeValues.unshift("openid");
      }
      scope = scopeValues.join(" ");

      const params = new URLSearchParams([
        ["client_id", kc.clientId],
        ["redirect_uri", redirectUri],
        ["state", state],
        ["nonce", nonce],
        ["response_mode", kc.responseMode || "query"],
        ["response_type", kc.responseType || "code"],
        ["scope", scope],
        ["code_challenge", codeChallenge],
        ["code_challenge_method", "S256"],
      ]);

      if (options.prompt) {
        params.append("prompt", options.prompt);
      }
      if (this.locale) {
        params.append("ui_locales", this.locale);
      }

      let authorizeUrl;
      const endpoints = kc.endpoints;
      if (endpoints && typeof endpoints.authorize === "function") {
        authorizeUrl = endpoints.authorize();
      } else {
        authorizeUrl = `${kc.authServerUrl || kc.url}realms/${kc.realm}/protocol/openid-connect/auth`;
      }

      const loginUrl = `${authorizeUrl}?${params.toString()}`;
      window.localStorage.setItem(
        `kc-callback-${state}`,
        JSON.stringify({
          state,
          nonce,
          redirectUri,
          loginOptions: options,
          pkceCodeVerifier: codeVerifier,
          expires: Date.now() + 60 * 60 * 1000,
        })
      );
      const startedHost = String(window.location.host);

      window.location.assign(loginUrl);

      // The first attempt can be cancelled by F7's history handling while the
      // app finishes mounting (history API calls cancel pending navigations).
      // Retry once mount has settled, then fall back to a new tab.
      setTimeout(() => {
        if (String(window.location.host) === startedHost) {
          console.warn(
            "[keycloak-store] first redirect attempt cancelled (host still " +
              startedHost +
              "); retrying with location.replace"
          );
          window.location.replace(loginUrl);
        }
      }, 1500);
      setTimeout(() => {
        if (String(window.location.host) === startedHost) {
          console.warn(
            "[keycloak-store] redirect still not applied; opening login in a new tab"
          );
          window.open(loginUrl, "_blank", "noopener");
        }
      }, 4000);
    },

    async logout(options) {
      if (!this.keycloak) {
        throw new Error("Keycloak instance is not initialized");
      }
      try {
        if (typeof options === "string") {
          return await this.keycloak.logout({ redirectUri: options });
        }
        return await this.keycloak.logout(options);
      } finally {
        this.authenticated = false;
        this._stopTokenRefresh();
        this._clearPersistedTokens();
      }
    },

    async forceLocalLogout() {
      if (this.keycloak) {
        try {
          this.keycloak.clearToken();
        } catch (error) {
          console.error("Failed to clear keycloak token", error);
        }
      }
      this._stopTokenRefresh();
      this._clearPersistedTokens();
      this.authenticated = false;
      this.token = "";
      this.refreshToken = "";
      this.idToken = "";
      this.tokenParsed = null;
      this.timeSkew = 0;
      this.userInfo = null;
      this.subject = null;
    },

    async register(options) {
      if (!this.keycloak) {
        throw new Error("Keycloak instance is not initialized");
      }
      if (typeof options === "string") {
        return this.keycloak.register({ redirectUri: options, locale: this.locale });
      }
      return this.keycloak.register({ ...options, locale: this.locale });
    },

    async loadUserInfo() {
      if (!this.keycloak || !this.keycloak.authenticated) {
        throw new Error("Keycloak instance is not initialized");
      }

      if (this.keycloak.userInfo) {
        this.userInfo = this.keycloak.userInfo;
        this._syncFromInstance();
        return this.userInfo;
      }

      if (!this.isNativePlatform) {
        const claims = this.keycloak.tokenParsed || {};
        const fallback = {
          sub: claims.sub,
          email: claims.email,
          email_verified: claims.email_verified,
          name: claims.name,
          given_name: claims.given_name,
          family_name: claims.family_name,
          preferred_username: claims.preferred_username,
          countryCode: claims.countryCode,
          PIN: claims.PIN,
        };
        const hasCoreClaims = Boolean(fallback.sub) || Boolean(fallback.email);

        if (hasCoreClaims) {
          this.userInfo = fallback;
          this._syncFromInstance();
          return this.userInfo;
        }

        // Token had no usable claims; try the endpoint, bounded so a stalled
        // request can never block the app on web/PWA.
        try {
          const userInfo = await withTimeout(
            this.keycloak.loadUserInfo(),
            USERINFO_TIMEOUT_MS,
            "Keycloak userinfo request timed out"
          );
          this.userInfo = userInfo || null;
          this._syncFromInstance();
          return this.userInfo;
        } catch (error) {
          console.warn("Failed to load user info from endpoint", error);
          throw error;
        }
      }

      try {
        const userInfo = await this.keycloak.loadUserInfo();
        this.userInfo = userInfo || null;
        this._syncFromInstance();
        return userInfo;
      } catch (error) {
        console.error("Failed to load user info", error);
        throw error;
      }
    },

    async loadUserProfile() {
      if (!this.keycloak) {
        throw new Error("Keycloak instance is not initialized");
      }

      // Deduplicate concurrent calls (e.g. init + onAuthSuccess)
      if (this._profileLoading) return this._profileLoading;

      this._profileLoading = (async () => {
        try {
          const raw =
            (await this.keycloak.loadUserProfile()) || null;

          // Keycloak's account endpoint uses firstName/lastName and nests
          // custom fields under `attributes` (as arrays); the app expects a
          // claim-style shape (given_name/family_name/countryCode...).
          const rawProfile = raw && typeof raw === "object" ? raw : {};
          const claims = this.userInfo || this.tokenParsed || {};
          const flatAttributes = {};
          Object.entries(rawProfile.attributes || {}).forEach(
            ([key, value]) => {
              flatAttributes[key] = Array.isArray(value) ? value[0] : value;
            },
          );

          this.profile = {
            ...claims,
            ...rawProfile,
            ...flatAttributes,
            sub: rawProfile.sub || rawProfile.id || claims.sub || null,
            name: rawProfile.name || claims.name || null,
            username: rawProfile.username || claims.preferred_username || null,
            email: rawProfile.email || claims.email || null,
            given_name:
              rawProfile.given_name ||
              rawProfile.givenName ||
              rawProfile.firstName ||
              claims.given_name ||
              "",
            family_name:
              rawProfile.family_name ||
              rawProfile.lastName ||
              claims.family_name ||
              "",
            countryCode:
              flatAttributes.countryCode ||
              rawProfile.countryCode ||
              claims.countryCode ||
              null,
            currencyCode:
              flatAttributes.currencyCode ||
              rawProfile.currencyCode ||
              claims.currencyCode ||
              null,
          };
          return this.profile;
        } catch (error) {
          console.error("Failed to load user profile", error);
          // Fall back to token claims so consumers still have a usable shape
          this.profile = this.userInfo || this.tokenParsed || null;
          throw error;
        } finally {
          this._profileLoading = null;
        }
      })();

      return this._profileLoading;
    },

    reset() {
      this._stopTokenRefresh();
      this._clearPersistedTokens();

      if (this.isNativePlatform && this.keycloak && typeof this.keycloak.clearToken === "function") {
        try {
          void this.keycloak.clearToken();
        } catch (error) {
          console.error("Failed to clear native keycloak session", error);
        }
      }

      if (typeof window !== "undefined") {
        delete window._keycloak;
      }

      this.keycloak = null;
      this.locale = navigator.language?.split("-")[0] || "en";
      this.initialized = false;
      this.authenticated = false;
      this.token = "";
      this.refreshToken = "";
      this.idToken = "";
      this.tokenParsed = null;
      this.timeSkew = 0;
      this.userInfo = null;
      this.subject = null;
      this.profile = null;
      this.user = null;
      this.initError = null;
      this._initializing = null;
      this._coldStartCompleted = false;
    },
  },
});
